export const sleep = (ms: number) => new Promise((r) => setTimeout(r, ms));

/**
 * Rejection reason when a `sleepUntil` poll is cancelled through its signal.
 * Deliberately distinguishable from the bare `reject()` a real timeout produces:
 * a caller must be able to tell "we gave up waiting" (worth reporting) from
 * "nobody is waiting any more" (nothing to report).
 */
export class SleepUntilAbortedError extends Error {
	constructor() {
		super('sleepUntil aborted');
		this.name = 'SleepUntilAbortedError';
	}
}

export const sleepUntil = async (func: () => boolean, timeoutMs: number, signal?: AbortSignal) => {
	return new Promise<void>((resolve, reject) => {
		if (signal?.aborted) {
			reject(new SleepUntilAbortedError());
			return;
		}

		const startTime = Date.now();
		const interval = setInterval(() => {
			if (func()) {
				clearInterval(interval);
				resolve();
			} else if (Date.now() - startTime > timeoutMs) {
				// Timedout
				clearInterval(interval);
				reject();
			}
		}, 20);

		// Without a signal the interval outlives whoever started the poll, and
		// `func` keeps running against a world that has moved on — reaching for
		// `window` after a React unmount, or after Vitest has torn the jsdom
		// environment down (which surfaces as an unhandled "window is not
		// defined" and fails the whole run).
		signal?.addEventListener(
			'abort',
			() => {
				clearInterval(interval);
				reject(new SleepUntilAbortedError());
			},
			{ once: true }
		);
	});
};
